import { AgGridReact, AgGridReactProps } from '@ag-grid-community/react';
import { Box, Flex } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    FilterChangedEvent,
    GridReadyEvent,
    IRowNode,
    RowClickedEvent,
    RowSelectedEvent,
    SelectionChangedEvent,
    SortChangedEvent,
} from '@ag-grid-community/core';

import { ResponsiveColDef, ResponsiveGridProps } from './types';
import { normalizeColumns } from './normalizeColumns';
import { useResponsiveGridMode } from './useResponsiveGridMode';
import { useResponsiveGridState } from './useResponsiveGridState';
import { createResponsiveRowSelectedEvent } from './events';
import { CardFilterBar } from './CardFilterBar';
import { CardListView } from './CardListView';
import { InfiniteCardListView } from './InfiniteCardListView';
import { CardSelectionApi } from './useCardSelection';

export function ResponsiveGrid<TData, TColId extends string = string>(props: ResponsiveGridProps<TData, TColId>) {
    const {
        columnDefs,
        defaultColDef,
        rowData,
        rowModelType,
        datasource,
        rowSelection,
        suppressCellFocus,
        suppressRowClickSelection,
        suppressRowHoverHighlight,
        enableCellTextSelection,
        autoSizeStrategy,
        onRowSelected,
        onSelectionChanged,
        onRowClicked,
        onGridReady,
        onFilterChanged,
        onRowDataUpdated,
        isRowSelectable,
        cacheBlockSize,
        maxBlocksInCache,
        rowBuffer,
        infiniteInitialRowCount,
        maxConcurrentDatasourceRequests,
        doesExternalFilterPass,
        isExternalFilterPresent,
        rowClassRules,
        gridRef,
        domLayout,
        rowHeight,
        pinnedBottomRowData,
        loading,
        suppressRowTransform,
        getRowStyle,
        getRowClass,
        suppressHorizontalScroll,
        responsive,
        cardView,
        initialState,
        onResponsiveRowSelected,
    } = props;

    const mode = useResponsiveGridMode(responsive);

    const columns = useMemo(
        () => normalizeColumns<TData, TColId>(columnDefs as ResponsiveColDef<TData, TColId>[], defaultColDef),
        [columnDefs, defaultColDef],
    );

    const { state, stateVersion, initialApplied, markInitialApplied, updateSortModel, updateFilterModel, updateFilter } =
        useResponsiveGridState({
            initialState,
        });

    const internalGridRef = useRef<AgGridReact<TData>>(null);

    // Guard to suppress shared-state feedback loops when applying state to the grid
    const applyingStateRef = useRef(false);

    // Apply shared state (or initial state on first mount) to AG Grid on grid ready
    const handleGridReady = useCallback(
        (event: GridReadyEvent<TData>) => {
            applyingStateRef.current = true;

            const filterToApply = initialApplied ? state.filterModel : (initialState?.filterModel ?? state.filterModel);
            const sortToApply = initialApplied ? state.sortModel : (initialState?.sortModel ?? state.sortModel);

            if (filterToApply && Object.keys(filterToApply).length > 0) {
                event.api.setFilterModel(filterToApply);
            }
            if (sortToApply && sortToApply.length > 0) {
                event.api.applyColumnState({
                    state: sortToApply.map((s, i) => ({
                        colId: s.colId,
                        sort: s.sort,
                        sortIndex: i,
                    })),
                    defaultState: { sort: null },
                });
            }

            if (!initialApplied) {
                markInitialApplied();
            }

            applyingStateRef.current = false;
            onGridReady?.(event);
        },
        [
            initialApplied,
            initialState?.filterModel,
            initialState?.sortModel,
            state.filterModel,
            state.sortModel,
            markInitialApplied,
            onGridReady,
        ],
    );

    // Wrap AG Grid onFilterChanged to sync filter state into shared state
    const handleAgFilterChanged = useCallback(
        (event: FilterChangedEvent<TData>) => {
            if (applyingStateRef.current) {
                return;
            }
            const model = event.api.getFilterModel();
            updateFilterModel(model ?? {});
            onFilterChanged?.(event);
        },
        [updateFilterModel, onFilterChanged],
    );

    // Wrap AG Grid onSortChanged to sync sort state into shared state
    const handleAgSortChanged = useCallback(
        (event: SortChangedEvent<TData>) => {
            if (applyingStateRef.current) {
                return;
            }
            const colState = event.api.getColumnState();
            const sortModel = colState
                .filter((c) => c.sort != null)
                .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
                .map((c) => ({ colId: c.colId, sort: c.sort as 'asc' | 'desc' }));
            updateSortModel(sortModel);
        },
        [updateSortModel],
    );

    // Wrap AG Grid onRowSelected to also fire onResponsiveRowSelected
    const handleAgRowSelected = useCallback(
        (event: RowSelectedEvent<TData>) => {
            onRowSelected?.(event);

            if (onResponsiveRowSelected && event.node.isSelected() !== undefined) {
                const wrappedEvent = createResponsiveRowSelectedEvent(event.data, event.rowIndex ?? -1, event.node.isSelected() ?? false);
                onResponsiveRowSelected(wrappedEvent);
            }
        },
        [onRowSelected, onResponsiveRowSelected],
    );

    // Card mode selection ref — gives ResponsiveGrid access to the selection API
    const cardSelectionRef = useRef<CardSelectionApi<TData> | null>(null);

    // Normalize rowSelection to 'single' | 'multiple' | undefined for card mode
    const cardRowSelection = useMemo<'single' | 'multiple' | undefined>(() => {
        if (typeof rowSelection === 'string') {
            return rowSelection === 'multiple' ? 'multiple' : 'single';
        }
        return undefined;
    }, [rowSelection]);

    // Wrap AG Grid's isRowSelectable (takes IRowNode) for card mode (takes { data })
    const cardIsRowSelectable = useMemo(() => {
        if (!isRowSelectable) {
            return undefined;
        }
        return (params: { data: TData | undefined }) => {
            const nodeShim = { data: params.data } as IRowNode<TData>;
            return isRowSelectable(nodeShim);
        };
    }, [isRowSelectable]);

    // Ref to updateFilterModel so the shim can call it lazily
    const updateFilterModelRef = useRef(updateFilterModel);
    updateFilterModelRef.current = updateFilterModel;

    // Ref to updateSortModel so the shim can call it lazily
    const updateSortModelRef = useRef(updateSortModel);
    updateSortModelRef.current = updateSortModel;

    // Build a shim API object that reads cardSelectionRef lazily at call time
    const cardShimApi = useMemo(
        () => ({
            getSelectedNodes: () => cardSelectionRef.current?.getSelectedNodes() ?? [],
            forEachNode: (cb: (node: unknown) => void) => cardSelectionRef.current?.forEachNode(cb as never),
            deselectAll: () => cardSelectionRef.current?.deselectAll(),
            setFilterModel: (model: Record<string, unknown> | null) => {
                updateFilterModelRef.current(model ?? {});
            },
            applyColumnState: (params: { state?: { colId: string; sort?: 'asc' | 'desc' | null; sortIndex?: number }[] }) => {
                if (!params.state) {
                    return;
                }
                const sortModel = params.state
                    .filter((c) => c.sort != null)
                    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
                    .map((c) => ({ colId: c.colId, sort: c.sort as 'asc' | 'desc' }));
                updateSortModelRef.current(sortModel);
            },
        }),
        [],
    );

    // Card mode onSelectionChanged — fires the consumer's callback with a shim event
    const handleCardSelectionChanged = useCallback(() => {
        if (!onSelectionChanged) {
            return;
        }

        const shimEvent = {
            type: 'selectionChanged',
            api: cardShimApi,
            context: undefined,
        } as unknown as SelectionChangedEvent<TData>;
        onSelectionChanged(shimEvent);
    }, [onSelectionChanged, cardShimApi]);

    // Card mode onRowClicked — creates a shim event for the consumer's callback
    const handleCardRowClicked = useCallback(
        (row: TData, rowIndex: number) => {
            if (!onRowClicked) {
                return;
            }
            const shimEvent = {
                type: 'rowClicked',
                data: row,
                node: { data: row, rowPinned: undefined },
                rowIndex,
                event: null,
                api: cardShimApi,
                context: undefined,
            } as unknown as RowClickedEvent<TData>;
            onRowClicked(shimEvent);
        },
        [onRowClicked, cardShimApi],
    );

    // Card mode onRowSelected — creates a shim RowSelectedEvent for the consumer's callback
    const handleCardRowSelected = useCallback(
        (row: TData, rowIndex: number, selected: boolean) => {
            if (onRowSelected) {
                const shimEvent = {
                    type: 'rowSelected',
                    data: row,
                    node: {
                        data: row,
                        isSelected: () => selected,
                        rowPinned: undefined,
                    },
                    rowIndex,
                    event: null,
                    api: cardShimApi,
                    context: undefined,
                } as unknown as RowSelectedEvent<TData>;
                onRowSelected(shimEvent);
            }

            if (onResponsiveRowSelected) {
                const wrappedEvent = createResponsiveRowSelectedEvent(row, rowIndex, selected);
                onResponsiveRowSelected(wrappedEvent);
            }
        },
        [onRowSelected, onResponsiveRowSelected, cardShimApi],
    );

    // Fire onGridReady in card mode with a shim API on mount and every transition into card mode
    const prevModeRef = useRef<'grid' | 'cards' | null>(null);
    useEffect(() => {
        const prevMode = prevModeRef.current;
        prevModeRef.current = mode;

        if (mode !== 'grid' && onGridReady && prevMode !== mode) {
            const shimEvent = {
                type: 'gridReady',
                api: cardShimApi,
                context: undefined,
            } as unknown as GridReadyEvent<TData>;
            onGridReady(shimEvent);
        }
    }, [mode, onGridReady, cardShimApi]);

    // Fire onFilterChanged in card mode when stateVersion changes
    const prevStateVersionRef = useRef(stateVersion);
    useEffect(() => {
        if (mode === 'grid') {
            return;
        }
        if (prevStateVersionRef.current !== stateVersion) {
            prevStateVersionRef.current = stateVersion;
            onFilterChanged?.({
                type: 'filterChanged',
                api: cardShimApi,
                context: undefined,
            } as unknown as FilterChangedEvent<TData>);
        }
    }, [mode, stateVersion, onFilterChanged, cardShimApi]);

    // Fire onRowDataUpdated in card mode when rowData changes
    const prevRowDataRef = useRef(rowData);
    useEffect(() => {
        if (mode === 'grid') {
            return;
        }
        if (prevRowDataRef.current !== rowData) {
            prevRowDataRef.current = rowData;
            onRowDataUpdated?.({
                type: 'rowDataUpdated',
                api: cardShimApi,
                context: undefined,
            } as unknown as Parameters<NonNullable<typeof onRowDataUpdated>>[0]);
        }
    }, [mode, rowData, onRowDataUpdated, cardShimApi]);

    if (mode === 'grid') {
        const agGridProps: AgGridReactProps<TData> = {
            columnDefs,
            defaultColDef,
            rowData,
            rowModelType,
            datasource,
            rowSelection,
            suppressCellFocus,
            suppressRowClickSelection,
            suppressRowHoverHighlight,
            enableCellTextSelection,
            autoSizeStrategy,
            onRowSelected: handleAgRowSelected,
            onSelectionChanged,
            onRowClicked,
            onGridReady: handleGridReady,
            onFilterChanged: handleAgFilterChanged,
            onSortChanged: handleAgSortChanged,
            onRowDataUpdated,
            isRowSelectable,
            cacheBlockSize,
            maxBlocksInCache,
            rowBuffer,
            infiniteInitialRowCount,
            maxConcurrentDatasourceRequests,
            doesExternalFilterPass,
            isExternalFilterPresent,
            rowClassRules,
            domLayout,
            rowHeight,
            pinnedBottomRowData,
            loading,
            suppressRowTransform,
            getRowStyle,
            getRowClass,
            suppressHorizontalScroll,
        };

        const cleanProps = Object.fromEntries(Object.entries(agGridProps).filter(([_, v]) => v !== undefined)) as AgGridReactProps<TData>;

        return (
            <Flex
                flexDirection="column"
                className="ag-theme-quartz"
                css={{
                    '--ag-background-color': { _dark: 'colors.musa.dark.500' },
                    '--ag-header-background-color': { _dark: 'colors.musa.dark.500' },
                    '--ag-foreground-color': { _dark: 'musa.dark.label' },
                    '--ag-header-foreground-color': { _dark: 'musa.dark.label' },
                    '--ag-border-color': { _dark: 'colors.musa.dark.border' },
                }}
                h="100%">
                <AgGridReact<TData> {...cleanProps} ref={gridRef ?? internalGridRef} />
            </Flex>
        );
    }

    const isInfinite = rowModelType === 'infinite';

    return (
        <Flex flexDirection="column" h="100%" minH={0} overflow="hidden" gap="0">
            <CardFilterBar columns={columns} state={state} onSortChange={updateSortModel} onFilterChange={updateFilter} />

            <Box position="relative" flex="1" minH={0}>
                <Box position="absolute" top={0} left={0} right={0} bottom={0}>
                    {isInfinite ? (
                        <InfiniteCardListView<TData, TColId>
                            columns={columns}
                            datasource={datasource}
                            state={state}
                            stateVersion={stateVersion}
                            cardView={cardView}
                            cacheBlockSize={cacheBlockSize}
                            maxConcurrentDatasourceRequests={maxConcurrentDatasourceRequests}
                            rowSelection={cardRowSelection}
                            suppressRowClickSelection={suppressRowClickSelection}
                            isRowSelectable={cardIsRowSelectable}
                            onSelectionChanged={handleCardSelectionChanged}
                            selectionRef={cardSelectionRef}
                            onRowClicked={handleCardRowClicked}
                            onRowSelected={handleCardRowSelected}
                        />
                    ) : (
                        <CardListView<TData, TColId>
                            columns={columns}
                            rowData={rowData ?? []}
                            state={state}
                            cardView={cardView}
                            rowSelection={cardRowSelection}
                            suppressRowClickSelection={suppressRowClickSelection}
                            isRowSelectable={cardIsRowSelectable}
                            onSelectionChanged={handleCardSelectionChanged}
                            selectionRef={cardSelectionRef}
                            doesExternalFilterPass={doesExternalFilterPass}
                            isExternalFilterPresent={isExternalFilterPresent}
                            onRowClicked={handleCardRowClicked}
                            onRowSelected={handleCardRowSelected}
                        />
                    )}
                </Box>
            </Box>
        </Flex>
    );
}
